import { createRootRoute, Link, Outlet, useRouterState } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { UserButton, SignedIn, SignedOut, SignInButton, SignUpButton, useAuth } from '@clerk/clerk-react'
import { PreviewDataInitializer } from '@/components/PreviewDataInitializer'
import { UserStoreInitializer } from '@/components/UserStoreInitializer'
import { PostHogPageView } from '@/components/PostHogPageView'
import { useState } from 'react'

export const Route = createRootRoute({
  component: RootComponent,
})

function RootComponent() {
  const router = useRouterState()
  const isSplashPage = router.location.pathname === '/'
  const { isSignedIn } = useAuth()
  
  // Determine route prefix based on auth status
  const routePrefix = isSignedIn ? '/app' : '/preview'

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      <PreviewDataInitializer />
      <UserStoreInitializer />
      <PostHogPageView />
      {/* Only show navigation when NOT on splash page */}
      {!isSplashPage && (
        <NavBar routePrefix={routePrefix} />
      )}

      <main className={isSplashPage ? '' : 'max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8'}>
        <Outlet />
      </main>
      <TanStackRouterDevtools />
    </div>
  )
}

function NavBar({ routePrefix }: { routePrefix: string }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isCreateDropdownOpen, setIsCreateDropdownOpen] = useState(false)

  const navLinks = [
    { to: `${routePrefix}/onboarding`, label: '[START HERE]', highlight: true },
    { to: `${routePrefix}/decks`, label: '[DECKS]' },
    { to: `${routePrefix}/schedule`, label: '[SCHEDULE]' },
    { to: `${routePrefix}/flashcards`, label: '[FLASHCARDS]' },
    { to: `${routePrefix}/songs`, label: '[SONGS]' },
    { to: `${routePrefix}/songs/library`, label: '[SONG LIBRARY]' },
  ]

  const createDropdownLinks = [
    { to: `${routePrefix}/audio-review`, label: '[AUDIO REVIEW]' },
    { to: `${routePrefix}/music-styles`, label: '[MUSIC STYLES]' },
    { to: `${routePrefix}/upload`, label: '[UPLOAD]' },
    { to: `${routePrefix}/lyrics`, label: '[LYRICS]' },
    { to: `${routePrefix}/instrumental`, label: '[INSTRUMENTAL]' },
  ]

  return (
    <nav className="bg-black border-b-4 border-sky-blue shadow-lg relative">
      <div className="w-full pl-6 pr-2">
        <div className="flex justify-between items-center h-20">
          {/* Site Title - Left Side */}
          <Link
            to="/"
            className="text-sky-blue hover:text-terminal-green transition-colors retro-glow mr-6"
          >
            <div className="flex flex-col">
              <span className="text-xl sm:text-2xl font-bold tracking-wider" style={{ fontFamily: 'Daffiys, monospace' }}>
                Jinglearn
              </span>
              {/* <span className="text-xs sm:text-sm font-mono text-trail-tan/80 hidden sm:block">
                music + memory = learn anything faster
              </span> */}
            </div>
          </Link>

          {/* Desktop Navigation - Hidden on mobile */}
          <div className="hidden lg:flex lg:items-center lg:space-x-3 lg:gap-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`inline-flex items-center gap-2 px-1 py-2 border-b-4 text-lg font-bold font-mono tracking-wider relative ${link.highlight ? 'animate-pulse' : ''}`}
                activeProps={{
                  className: 'border-terminal-green text-terminal-green retro-glow',
                }}
                inactiveProps={{
                  className: link.highlight
                    ? 'border-transparent text-terminal-green hover:border-terminal-green hover:text-terminal-green transition-all retro-glow'
                    : 'border-transparent text-trail-tan hover:border-sky-blue hover:text-sky-blue transition-all',
                }}
              >
                {link.label}
              </Link>
            ))}

            {/* Create Dropdown - Desktop */}
            <div className="relative">
              <button
                onClick={() => setIsCreateDropdownOpen(!isCreateDropdownOpen)}
                onBlur={() => setTimeout(() => setIsCreateDropdownOpen(false), 200)}
                className="inline-flex items-center px-1 py-2 border-b-4 border-transparent text-lg font-bold font-mono tracking-wider text-trail-tan hover:border-sky-blue hover:text-sky-blue transition-all"
              >
                [CREATE]
                <svg
                  className={`ml-1 w-4 h-4 transition-transform ${isCreateDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isCreateDropdownOpen && (
                <div className="absolute left-0 mt-2 w-56 bg-black border-2 border-sky-blue shadow-xl z-50">
                  {createDropdownLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setIsCreateDropdownOpen(false)}
                      className="block px-4 py-3 text-trail-tan font-bold font-mono text-base tracking-wider hover:bg-sky-blue/30 hover:text-terminal-green transition-all border-b border-sky-blue/30 last:border-b-0"
                      activeProps={{
                        className: 'bg-terminal-green/20 text-terminal-green',
                      }}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Side: Auth buttons + Mobile menu button */}
          <div className="flex items-center space-x-2 ml-4">
            {/* Authentication buttons */}
            <div className="hidden sm:flex items-center space-x-2">
              <SignedIn>
                <div className="border-2 border-sky-blue rounded-lg p-1 retro-glow">
                  <UserButton afterSignOutUrl="/" />
                </div>
              </SignedIn>
              <SignedOut>
                {routePrefix === '/preview' ? (
                  // Preview mode: Only show SIGN UP button
                  <SignUpButton mode="modal" forceRedirectUrl="/app/onboarding">
                    <button className="px-4 sm:px-6 py-2 bg-sky-blue text-black text-sm sm:text-base font-bold border-2 border-sky-blue hover:bg-terminal-green hover:border-terminal-green transition-all tracking-wider retro-glow">
                      [SIGN UP]
                    </button>
                  </SignUpButton>
                ) : (
                  // App mode: Show both SIGN IN and SIGN UP
                  <>
                    <SignInButton mode="modal">
                      <button className="px-4 sm:px-6 py-2 text-sm sm:text-base font-bold text-trail-tan border-2 border-trail-tan hover:bg-trail-tan hover:text-black transition-all tracking-wider">
                        [SIGN IN]
                      </button>
                    </SignInButton>
                    <SignUpButton mode="modal" forceRedirectUrl="/app/onboarding">
                      <button className="px-4 sm:px-6 py-2 bg-sky-blue text-black text-sm sm:text-base font-bold border-2 border-sky-blue hover:bg-terminal-green hover:border-terminal-green transition-all tracking-wider retro-glow">
                        [SIGN UP]
                      </button>
                    </SignUpButton>
                  </>
                )}
              </SignedOut>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-sky-blue hover:text-terminal-green transition-colors border-2 border-sky-blue rounded"
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-black border-t-4 border-sky-blue z-50">
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-2 px-4 py-3 border-2 font-bold font-mono text-base tracking-wider transition-all relative ${
                    link.highlight
                      ? 'bg-terminal-green/20 border-terminal-green text-terminal-green animate-pulse'
                      : 'bg-sky-blue/20 border-sky-blue text-trail-tan hover:bg-sky-blue/40 hover:text-terminal-green'
                  }`}
                  activeProps={{
                    className: 'bg-terminal-green/30 border-terminal-green text-terminal-green',
                  }}
                >
                  {link.label}
                </Link>
              ))}

              {/* Create Dropdown - Mobile */}
              <div className="space-y-2">
                <button
                  onClick={() => setIsCreateDropdownOpen(!isCreateDropdownOpen)}
                  className="w-full px-4 py-3 bg-sky-blue/20 border-2 border-sky-blue text-trail-tan font-bold font-mono text-base tracking-wider hover:bg-sky-blue/40 hover:text-terminal-green transition-all flex items-center justify-between"
                >
                  [CREATE]
                  <svg
                    className={`w-5 h-5 transition-transform ${isCreateDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isCreateDropdownOpen && (
                  <div className="pl-4 space-y-2">
                    {createDropdownLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={() => {
                          setIsCreateDropdownOpen(false)
                          setIsMobileMenuOpen(false)
                        }}
                        className="block px-4 py-2 bg-black border-l-4 border-terminal-green text-trail-tan font-bold font-mono text-sm tracking-wider hover:bg-terminal-green/20 hover:text-terminal-green transition-all"
                        activeProps={{
                          className: 'bg-terminal-green/30 text-terminal-green',
                        }}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Auth Buttons */}
              <div className="pt-4 border-t-2 border-sky-blue space-y-2">
                <SignedIn>
                  <div className="px-4 py-2 bg-sky-blue/20 border-2 border-sky-blue rounded">
                    <UserButton afterSignOutUrl="/" />
                  </div>
                </SignedIn>
                <SignedOut>
                  <SignInButton mode="modal">
                    <button className="w-full px-4 py-3 bg-sky-blue/20 border-2 border-sky-blue text-trail-tan font-bold font-mono text-base tracking-wider hover:bg-sky-blue/40 hover:text-terminal-green transition-all">
                      [SIGN IN]
                    </button>
                  </SignInButton>
                  <SignUpButton mode="modal" forceRedirectUrl="/app/onboarding">
                    <button className="w-full px-4 py-3 bg-terminal-green/30 border-2 border-terminal-green text-terminal-green font-bold font-mono text-base tracking-wider hover:bg-terminal-green/50 transition-all mt-2">
                      [SIGN UP]
                    </button>
                  </SignUpButton>
                </SignedOut>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}
